import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import XLSX from "xlsx-js-style";
import { DateTime } from "luxon";
import { useAuth } from "../context/AuthContext";
import { availabilityAbbreviations } from "./AvailabilityTypePills";

const EXPORT_DAYS_PER_PAGE = 100;
const EXCEL_FONT_SIZE = 18;
const EXCEL_ROW_HEIGHT_POINTS = 30;

const filterLabel = (value, fallback) => value || fallback;

const sanitizeFileNamePart = (value) => {
  return value
    .toString()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_-]/g, "")
    .toLowerCase();
};

const availabilityNote = (availability) => {
  return availability?.user?.availabilityNote?.trim() || "";
};

const exportDate = (date) => DateTime.fromISO(date).toJSDate();

const roleFill = (roles) => {
  const role = `${roles || "N/A"}`.split(",")[0].trim();
  if (["RN", "RM", "RMN", "RNLD", "RSCN"].includes(role)) return "008FA6";
  if (["HCA2", "HCA3"].includes(role)) return "258A3F";
  if (["LDOC", "LCON"].includes(role)) return "861C3D";
  if (role === "N/A") return "5F6368";
  return "A61D8D";
};

const roleGroup = (roles) => {
  const userRoles = `${roles || ""}`.split(",").map((role) => role.trim());
  if (userRoles.some((role) => ["RN", "RM", "RMN", "RNLD", "RSCN"].includes(role))) return 0;
  if (userRoles.some((role) => ["HCA2", "HCA3"].includes(role))) return 1;
  if (userRoles.some((role) => ["LDOC", "LCON"].includes(role))) return 2;
  return 3;
};

const sortWorksheetRecords = (records) => {
  return [...records].sort((first, second) => {
    const dateComparison = first.date.localeCompare(second.date);
    if (dateComparison !== 0) return dateComparison;

    const roleComparison =
      roleGroup(first.availability?.user?.roles) - roleGroup(second.availability?.user?.roles);
    if (roleComparison !== 0) return roleComparison;

    const surnameComparison = (first.availability?.user?.surname || "").localeCompare(
      second.availability?.user?.surname || "",
    );
    if (surnameComparison !== 0) return surnameComparison;

    return (first.availability?.user?.forename || "").localeCompare(
      second.availability?.user?.forename || "",
    );
  });
};

const worksheetName = (name, usedNames) => {
  const baseName = `${name || "Unassigned"}`
    .replace(/[\\/*?:[\]]/g, " ")
    .trim()
    .slice(0, 31) || "Unassigned";
  let sheetName = baseName;
  let suffix = 2;
  while (usedNames.has(sheetName)) {
    sheetName = `${baseName.slice(0, 28)} ${suffix}`;
    suffix += 1;
  }
  usedNames.add(sheetName);
  return sheetName;
};

const setCellBorder = (cell, { firstRow, lastRow, dateEnds }) => {
  cell.s = {
    ...cell.s,
    font: { ...cell.s?.font, sz: EXCEL_FONT_SIZE },
    border: {
      top: { style: firstRow ? "medium" : "thin", color: { rgb: "000000" } },
      bottom: {
        style: lastRow ? "medium" : dateEnds ? "double" : "thin",
        color: { rgb: "000000" },
      },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    },
  };
};

const AvailabilityCardsExport = ({ dateRange, trust, role, disabled = false }) => {
  const { authAxios } = useAuth();
  const [exporting, setExporting] = useState(false);

  const getAllAvailability = async () => {
    if (!dateRange) return [];

    const requestPage = async (page) => {
      const response = await authAxios().post("/api/backoffice/availability/cards/paging", {
        ...dateRange,
        trustId: trust?.id || null,
        role: role?.value || null,
        page,
        daysPerPage: EXPORT_DAYS_PER_PAGE,
      });
      return response.data;
    };

    const firstPage = await requestPage(0);
    const totalPages = Math.ceil(
      (firstPage.totalElements || 0) / EXPORT_DAYS_PER_PAGE,
    );
    const pages = [firstPage.content || []];
    for (let page = 1; page < totalPages; page += 1) {
      const response = await requestPage(page);
      pages.push(response.content || []);
    }
    return pages.flat();
  };

  const getTrusts = async () => {
    if (trust) return [trust];

    const response = await authAxios().get("/api/trusts/");
    return response.data || [];
  };

  const createWorksheet = (trustLabel, roleLabel, records) => {
    const sortedRecords = sortWorksheetRecords(records);
    const rows = [
      [`${trustLabel} ${roleLabel}`],
      ["date", "Name", "role", "availability", "note"],
      ...sortedRecords.map(({ date, availability }) => [
        exportDate(date),
        `${availability?.user?.surname || ""}, ${availability?.user?.forename || ""}`.trim(),
        availability?.user?.roles || "N/A",
        availabilityAbbreviations(availability).split(" ").join(" / "),
        availabilityNote(availability),
      ]),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(rows, { cellDates: true });
    worksheet["!rows"] = rows.map(() => ({ hpt: EXCEL_ROW_HEIGHT_POINTS }));
    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 28 },
      { wch: 14 },
      { wch: 22 },
      { wch: 50 },
    ];
    worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
    worksheet["!autofilter"] = { ref: `A2:E${rows.length}` };
    worksheet.A1.s = {
      font: { bold: true, sz: EXCEL_FONT_SIZE },
    };

    for (let column = 0; column < 5; column += 1) {
      const header = worksheet[XLSX.utils.encode_cell({ r: 1, c: column })];
      header.s = {
        font: { bold: true, sz: EXCEL_FONT_SIZE },
        fill: { patternType: "solid", fgColor: { rgb: "EAF5F7" } },
        border: {
          top: { style: "medium", color: { rgb: "000000" } },
          bottom: { style: "medium", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
    }

    sortedRecords.forEach((record, index) => {
      const row = index + 2;
      const dateStarts = index === 0 || sortedRecords[index - 1].date !== record.date;
      const dateEnds =
        index === sortedRecords.length - 1 || sortedRecords[index + 1].date !== record.date;
      for (let column = 0; column < 5; column += 1) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
        setCellBorder(cell, {
          firstRow: index === 0,
          lastRow: index === sortedRecords.length - 1,
          dateEnds,
        });
      }

      worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
        ...worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })].s,
        font: {
          ...worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })].s.font,
          color: { rgb: dateStarts ? "FF0000" : "FFFFFF" },
        },
      };
      worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })].z = "d-m-yyyy, ddd";
      worksheet[XLSX.utils.encode_cell({ r: row, c: 2 })].s = {
        ...worksheet[XLSX.utils.encode_cell({ r: row, c: 2 })].s,
        fill: {
          patternType: "solid",
          fgColor: { rgb: roleFill(record.availability?.user?.roles) },
        },
      };
    });

    return worksheet;
  };

  const exportAvailability = async () => {
    try {
      setExporting(true);
      const [allAvailability, trusts] = await Promise.all([
        getAllAvailability(),
        getTrusts(),
      ]);
      if (allAvailability.length === 0) return;

      const trustLabel = filterLabel(trust?.shortName, "all_trusts");
      const roleLabel = filterLabel(role?.value, "all_roles");
      const records = allAvailability.flatMap((dayAvailability) =>
        dayAvailability.availability.map((availability) => ({
          date: dayAvailability.date,
          availability,
        })),
      );

      const workbook = XLSX.utils.book_new();
      const usedNames = new Set();
      const targetTrusts = trust ? trusts : [...trusts, { id: null, shortName: "Unassigned" }];
      targetTrusts.forEach((targetTrust) => {
        const trustRecords = records.filter(({ availability }) => {
          const trustIds = availability?.user?.trustIds || [];
          return targetTrust.id === null
            ? trustIds.length === 0
            : trustIds.includes(targetTrust.id);
        });
        if (trustRecords.length === 0) return;

        const sheetLabel = targetTrust.shortName || "Unassigned";
        const worksheet = createWorksheet(sheetLabel, roleLabel, trustRecords);
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          worksheetName(sheetLabel, usedNames),
        );
      });

      if (workbook.SheetNames.length === 0) return;
      XLSX.writeFile(
        workbook,
        `${sanitizeFileNamePart(trustLabel)}_${sanitizeFileNamePart(roleLabel)}_availability.xlsx`,
      );
    } catch (error) {
      console.error("Unable to export availability", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="outline-offblack"
      onClick={exportAvailability}
      disabled={disabled || exporting || !dateRange}
    >
      {exporting ? "Exporting..." : "Export"}
    </Button>
  );
};

export default AvailabilityCardsExport;
